import { useMemo, useState } from "react";
import LeaveManagementView from "./LeaveManagementView";
import {
  changeCurrentUserPassword,
  updateCurrentUserProfile,
  uploadCurrentUserAvatar
} from "../services/userService";

const validatePasswordRules = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-ZÇĞİÖŞÜ]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9ÇĞİÖŞÜçğıöşü]/.test(password)
  };

  return {
    checks,
    isValid: Object.values(checks).every(Boolean)
  };
};

function ProfileView({ user, userData, users = [], isAdmin = false, onUserDataUpdate, themeMode = "light" }) {
  const [name, setName] = useState(userData?.name || user?.displayName || "");
  const [avatarPreview, setAvatarPreview] = useState(userData?.photoURL || user?.photoURL || "");
  const [avatarFile, setAvatarFile] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [message, setMessage] = useState(null);

  const passwordValidation = useMemo(() => validatePasswordRules(newPassword), [newPassword]);
  const isDark = themeMode === "dark";

  const colors = {
    text: isDark ? "#e5edf6" : "#111827",
    heading: isDark ? "#e5edf6" : "#124254",
    muted: isDark ? "#94a3b8" : "#6b7280",
    label: isDark ? "#cbd5e1" : "#374151",
    card: isDark ? "#162235" : "#ffffff",
    soft: isDark ? "#1e293b" : "#f8fafc",
    input: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "#334155" : "#dbe2ea",
    cardBorder: isDark ? "#27364d" : "#e5e7eb",
    primary: isDark ? "#155e75" : "#124254",
    avatarBg: isDark ? "#0f172a" : "#eef6ff",
    avatarBorder: isDark ? "#334155" : "#dbeafe",
    successBg: isDark ? "#123423" : "#dcfce7",
    successBorder: isDark ? "#166534" : "#bbf7d0",
    successText: isDark ? "#86efac" : "#166534",
    errorBg: isDark ? "#3a1414" : "#fee2e2",
    errorBorder: isDark ? "#7f1d1d" : "#fecaca",
    errorText: isDark ? "#fca5a5" : "#991b1b"
  };

  const roleText =
    userData?.role === "admin"
      ? "Admin"
      : userData?.role === "editor"
      ? "Düzenleyici"
      : "Görüntüleyici";

  const accessText =
    (userData?.assignedCompanies || []).length > 0
      ? `${(userData?.assignedCompanies || []).length} firma atanmış`
      : "Firma ataması yok";

  const processAccessText =
    userData?.processProjectAccessMode === "assigned"
      ? `${(userData?.assignedProcessProjects || []).length} süreç projesi ile sınırlı`
      : "Tüm süreç projelerine erişim";

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showMessage("error", "Lütfen resim formatında bir dosya seçiniz.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showMessage("error", "Avatar görseli en fazla 2 MB olabilir.");
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setMessage(null);
  };

  const handleProfileSave = async () => {
    if (!name.trim()) {
      showMessage("error", "Ad soyad alanı boş bırakılamaz.");
      return;
    }

    setSavingProfile(true);
    setMessage(null);

    try {
      let photoURL = avatarPreview;

      if (avatarFile) {
        photoURL = await uploadCurrentUserAvatar({
          uid: user?.uid,
          file: avatarFile
        });
      }

      await updateCurrentUserProfile({
        uid: user?.uid,
        name,
        photoURL
      });

      setAvatarFile(null);
      setAvatarPreview(photoURL);
      onUserDataUpdate?.({
        ...(userData || {}),
        name: name.trim(),
        photoURL
      });
      showMessage("success", "Profil bilgileriniz güncellendi.");
    } catch (error) {
      showMessage("error", error.message || "Profil güncellenirken bir hata oluştu.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showMessage("error", "Şifre değiştirmek için tüm alanları doldurunuz.");
      return;
    }

    if (!passwordValidation.isValid) {
      showMessage("error", "Yeni şifre kuralları karşılamıyor.");
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage("error", "Yeni şifre ve tekrar alanı aynı olmalıdır.");
      return;
    }

    setChangingPassword(true);
    setMessage(null);

    try {
      await changeCurrentUserPassword({
        currentPassword,
        newPassword
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      showMessage("success", "Şifreniz başarıyla değiştirildi.");
    } catch (error) {
      const text =
        error.code === "auth/invalid-credential" || error.code === "auth/wrong-password"
          ? "Mevcut şifreniz hatalı. Lütfen tekrar deneyiniz."
          : error.message || "Şifre değiştirilirken bir hata oluştu.";

      showMessage("error", text);
    } finally {
      setChangingPassword(false);
    }
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    fontSize: 14,
    outline: "none",
    background: colors.input,
    color: colors.text
  };

  const cardStyle = {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 20,
    padding: 24,
    boxShadow: isDark ? "0 10px 28px rgba(0, 0, 0, 0.22)" : "0 10px 28px rgba(15, 23, 42, 0.06)"
  };

  const primaryButtonStyle = {
    border: "none",
    borderRadius: 12,
    padding: "12px 16px",
    background: colors.primary,
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer"
  };

  const ruleItem = (isValid, text) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: isValid ? colors.successText : colors.muted,
        fontSize: 13,
        fontWeight: 700
      }}
    >
      <span>{isValid ? "✓" : "○"}</span>
      <span>{text}</span>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, color: colors.text }}>Profilim</h1>
        <p style={{ marginTop: 8, color: colors.muted }}>
          Hesap bilgileriniz, şifre işlemleri ve izin talepleriniz
        </p>
      </div>

      {message && (
        <div
          style={{
            marginBottom: 18,
            padding: "12px 14px",
            borderRadius: 14,
            border: message.type === "success" ? `1px solid ${colors.successBorder}` : `1px solid ${colors.errorBorder}`,
            background: message.type === "success" ? colors.successBg : colors.errorBg,
            color: message.type === "success" ? colors.successText : colors.errorText,
            fontWeight: 800
          }}
        >
          {message.text}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 1fr)",
          gap: 20,
          marginBottom: 24
        }}
      >
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: colors.heading }}>Hesap Bilgileri</h2>

          <div style={{ display: "flex", gap: 18, alignItems: "center", marginBottom: 20 }}>
            <div
              style={{
                width: 92,
                height: 92,
                borderRadius: "50%",
                background: colors.avatarBg,
                border: `3px solid ${colors.avatarBorder}`,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.heading,
                fontSize: 30,
                fontWeight: 900,
                flexShrink: 0
              }}
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Kullanıcı avatarı"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                (name || user?.email || "?").slice(0, 1).toLocaleUpperCase("tr-TR")
              )}
            </div>

            <div>
              <label
                style={{
                  display: "inline-block",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 12,
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontWeight: 800,
                  color: colors.heading,
                  background: colors.soft
                }}
              >
                Avatar Seç
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: "none" }}
                />
              </label>
              <div style={{ color: colors.muted, fontSize: 12, marginTop: 8 }}>
                PNG/JPG/WebP, en fazla 2 MB
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <label style={{ fontWeight: 800, color: colors.label }}>
              Ad Soyad
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>

            <label style={{ fontWeight: 800, color: colors.label }}>
              E-posta
              <input value={user?.email || ""} disabled style={{ ...inputStyle, marginTop: 6, color: colors.muted, opacity: 0.82 }} />
            </label>

            <button
              type="button"
              onClick={handleProfileSave}
              disabled={savingProfile}
              style={{
                ...primaryButtonStyle,
                opacity: savingProfile ? 0.7 : 1,
                marginTop: 4
              }}
            >
              {savingProfile ? "Kaydediliyor..." : "Profili Kaydet"}
            </button>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: colors.heading }}>Şifre Değiştir</h2>

          <div style={{ display: "grid", gap: 12 }}>
            <input
              type="password"
              placeholder="Mevcut şifre"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Yeni şifre"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Yeni şifre tekrar"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              style={inputStyle}
            />

            <div
              style={{
                display: "grid",
                gap: 6,
                padding: 12,
                borderRadius: 14,
                background: colors.soft,
                border: `1px solid ${colors.cardBorder}`
              }}
            >
              {ruleItem(passwordValidation.checks.length, "En az 8 karakter")}
              {ruleItem(passwordValidation.checks.uppercase, "En az 1 büyük harf")}
              {ruleItem(passwordValidation.checks.number, "En az 1 sayı")}
              {ruleItem(passwordValidation.checks.special, "En az 1 özel karakter")}
            </div>

            <button
              type="button"
              onClick={handlePasswordChange}
              disabled={changingPassword}
              style={{
                ...primaryButtonStyle,
                opacity: changingPassword ? 0.7 : 1
              }}
            >
              {changingPassword ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, color: colors.heading }}>Yetki Özeti</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12
          }}
        >
          {[
            ["Rol", roleText],
            ["Firma ataması", accessText],
            ["Süreç erişimi", processAccessText],
            ["Hesap durumu", userData?.isActive === false ? "Pasif" : "Aktif"]
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                padding: 14,
                borderRadius: 14,
                background: colors.soft,
                border: `1px solid ${colors.cardBorder}`
              }}
            >
              <div style={{ color: colors.muted, fontSize: 12, fontWeight: 800 }}>{label}</div>
              <div style={{ color: colors.text, fontSize: 15, fontWeight: 900, marginTop: 4 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      <LeaveManagementView
        user={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
        users={users}
        isAdmin={isAdmin}
        themeMode={themeMode}
      />
    </div>
  );
}

export default ProfileView;
